import { useState, useEffect, useMemo } from 'react';
import { supabase } from '../supabaseClient';
import { fN } from './utils';

const nf = n => fN(n, 1, 0);
const pctFmt = n => (isFinite(n) ? `${n > 0 ? '+' : ''}${fN(n, 0)}%` : '—');
const signed = n => `${n >= 0 ? '+' : ''}${nf(n)} h`;

function groupRows(rows, kind, paceFactor) {
  const byName = {};
  for (const r of rows) {
    if (r.kind !== kind) continue;
    const g = byName[r.client] || (byName[r.client] = { name: r.client, rcTotal: 0, corTotal: 0, hasRC: false, people: [] });
    const rc = Number(r.rc_hours) || 0;
    const cor = Number(r.cor_hours) || 0;
    g.rcTotal += rc;
    g.corTotal += cor;
    if (r.in_plan) g.hasRC = true;
    g.people.push({ person: r.person, rc, cor, diff: cor - rc * paceFactor });
  }
  return Object.values(byName).map(g => {
    const expected = g.rcTotal * paceFactor;
    const diff = g.corTotal - expected;
    g.people.sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff));
    return { ...g, expected, diff, diffPct: expected > 0 ? (diff / expected) * 100 : (g.corTotal > 0 ? Infinity : 0) };
  });
}

export default function RateCardTab({ selMonth }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(() => new Set());

  useEffect(() => {
    if (!selMonth) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    supabase.rpc('rate_card_vs_cor', { p_year: selMonth.year, p_month: selMonth.month }).then(({ data: d, error: e }) => {
      if (cancelled) return;
      if (e) setError(e.message);
      else setData(d);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [selMonth]);

  const model = useMemo(() => {
    if (!data) return null;
    const p = data.period;
    const isCurrent = p.status === 'en curso';
    const pace = isCurrent ? p.days_elapsed / p.days_in_month : 1;
    const clients = groupRows(data.rows, 'client', pace).sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff));
    const internal = groupRows(data.rows, 'internal', pace).sort((a, b) => a.name.localeCompare(b.name));
    return { p, isCurrent, pace, clients, internal };
  }, [data]);

  if (loading) return <div style={{ padding: 60, textAlign: 'center', color: 'var(--text3)' }}>Cargando…</div>;
  if (error) return <div style={{ padding: 60, textAlign: 'center', color: 'var(--red)' }}>No se pudo cargar: {error}</div>;
  if (!model) return null;

  const { p, isCurrent, pace, clients, internal } = model;
  const firstPeriod = data.settings?.first_period;
  if (firstPeriod && new Date(selMonth.year, selMonth.month - 1, 1) < new Date(firstPeriod + 'T00:00:00')) {
    return <div style={{ padding: 60, textAlign: 'center', color: 'var(--text3)' }}>El rate card aplica desde {firstPeriod.slice(0, 7)}.</div>;
  }

  const planLabel = isCurrent ? 'Rate card (mes)' : 'Rate card';
  const realLabel = isCurrent ? 'COR (a la fecha)' : 'COR real';
  const diffLabel = isCurrent ? 'Vs. esperado' : 'Diferencia';

  const totalRC = clients.filter(c => c.hasRC).reduce((a, c) => a + c.rcTotal, 0);
  const totalCOR = clients.reduce((a, c) => a + c.corTotal, 0);
  const totalExpected = totalRC * pace;
  const totalDiff = totalCOR - totalExpected;
  const noRC = clients.filter(c => !c.hasRC && c.corTotal > 0);

  const kpis = [
    { label: isCurrent ? 'Rate card · mes completo' : 'Rate card asignado', value: `${nf(totalRC)} h`, sub: isCurrent ? `esperado a la fecha: ${nf(totalExpected)} h` : `${clients.filter(c => c.hasRC).length} clientes con rate card`, color: 'var(--text)' },
    { label: isCurrent ? 'COR real (a la fecha)' : 'COR real', value: `${nf(totalCOR)} h`, sub: isCurrent ? `día ${p.days_elapsed} de ${p.days_in_month}` : 'mes cerrado', color: 'var(--blue)' },
    { label: `Diferencia vs ${isCurrent ? 'ritmo esperado' : 'rate card'}`, value: signed(totalDiff), sub: pctFmt(totalExpected > 0 ? (totalDiff / totalExpected) * 100 : 0), color: totalDiff > 0 ? 'var(--red)' : 'var(--green)' },
    { label: 'Sin rate card, con horas', value: noRC.length, sub: `${nf(noRC.reduce((a, c) => a + c.corTotal, 0))} h reales sin plan`, color: 'var(--amber)' },
  ];

  const q = query.trim().toLowerCase();
  const filtered = clients.filter(c => !q || c.name.toLowerCase().includes(q) || c.people.some(x => x.person.toLowerCase().includes(q)));

  const toggle = key => setOpen(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const freshness = data.data_as_of ? new Date(data.data_as_of) : null;
  const fresh = freshness && (Date.now() - freshness.getTime()) / 36e5 <= 30;

  return (
    <>
      <div style={calloutStyle}>
        <span style={tagStyle}>Auto</span>
        <div>
          <strong>Horas reales COR</strong> se leen en vivo desde Supabase. <strong>Rate card</strong> (última carga:{' '}
          <strong>{data.settings?.as_of ? new Date(data.settings.as_of + 'T00:00:00').toLocaleDateString('es-AR', { day: '2-digit', month: 'long', year: 'numeric' }) : '—'}</strong>) queda fijo hasta que se cargue una versión nueva.
          {' '}Clientes de proyecto one-time y los proyectos one-time dentro de un cliente mensual
          {data.one_time_projects?.length ? ` (${data.one_time_projects.join(' · ')})` : ''} quedan fuera de este panel. Desplegá un cliente para ver sus notas.
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <div style={{ fontSize: 12.5, color: 'var(--text2)' }}>
          <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: fresh ? 'var(--green)' : 'var(--amber)', marginRight: 6 }} />
          {freshness ? `COR actualizado ${freshness.toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}` : 'COR actualizado —'}
          {' · '}{isCurrent ? `día ${p.days_elapsed} de ${p.days_in_month}` : 'mes cerrado'}
        </div>
        <input
          type="text" value={query} onChange={e => setQuery(e.target.value)}
          placeholder="Buscar cliente o persona…"
          style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 9, padding: '8px 12px', minWidth: 240, fontSize: 13, color: 'var(--text)' }}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 12, marginBottom: 20 }}>
        {kpis.map(k => (
          <div key={k.label} style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 16, textAlign: 'center' }}>
            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.7px', color: 'var(--text2)', marginBottom: 4 }}>{k.label}</div>
            <div style={{ fontFamily: 'DM Mono,monospace', fontSize: 22, fontWeight: 700, color: k.color, margin: '6px 0 2px' }}>{k.value}</div>
            <div style={{ fontSize: 11, color: 'var(--text3)' }}>{k.sub}</div>
          </div>
        ))}
      </div>

      <GroupTable
        title="Cliente" rows={filtered} open={open} onToggle={n => toggle('c:' + n)} prefix="c:"
        planLabel={planLabel} realLabel={realLabel} diffLabel={diffLabel}
        notes={data.notes} query={q} showNoRC
      />

      <h3 style={{ fontSize: 15, fontWeight: 700, margin: '28px 0 12px' }}>
        Funciones internas <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text3)' }}>(no cliente)</span>
      </h3>
      <GroupTable
        title="Función" rows={internal} open={open} onToggle={n => toggle('i:' + n)} prefix="i:"
        planLabel={planLabel} realLabel={realLabel} diffLabel={diffLabel}
      />

      <p style={{ fontSize: 11, color: 'var(--text3)', marginTop: 14, lineHeight: 1.6 }}>
        {p.internal_hours ? `Horas internas totales (incl. New Biz / Marketing / operaciones) excluidas del cuadro de clientes: ${nf(Number(p.internal_hours))} h. ` : ''}
        Fuente horas reales: tabla daily_hours · sync COR → Supabase (n8n). Nombres unificados entre rate card y COR: {data.alias_count} variantes reconciliadas.
      </p>
    </>
  );
}

function GroupTable({ title, rows, open, onToggle, prefix, planLabel, realLabel, diffLabel, notes, query, showNoRC }) {
  if (!rows.length) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)', fontSize: 13.5 }}>Sin resultados.</div>;
  }
  return (
    <div style={{ overflowX: 'auto', borderRadius: 12, border: '1px solid var(--border)', background: 'var(--card)' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: 'var(--card2)' }}>
            <th style={{ ...thStyle, width: 28 }}></th>
            <th style={thStyle}>{title}</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>{planLabel}</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>{realLabel}</th>
            <th style={thStyle}>Plan vs real</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>{diffLabel}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => {
            const key = prefix + r.name;
            const searchOpen = query && !r.name.toLowerCase().includes(query) && r.people.some(x => x.person.toLowerCase().includes(query));
            const isOpen = open.has(key) || searchOpen;
            const people = query && !r.name.toLowerCase().includes(query) ? r.people.filter(x => x.person.toLowerCase().includes(query)) : r.people;
            const scale = Math.max(1, r.rcTotal, r.corTotal) * 1.05;
            const fill = Math.min(100, (r.corTotal / scale) * 100);
            const marker = Math.min(100, (r.expected / scale) * 100);
            const over = r.diff > 0;
            return (
              <FragmentRow key={key}>
                <tr onClick={() => onToggle(r.name)} style={{ borderTop: '1px solid var(--border)', cursor: 'pointer' }}>
                  <td style={{ padding: '10px 14px', color: 'var(--text3)' }}>{isOpen ? '▾' : '▸'}</td>
                  <td style={{ padding: '10px 14px', fontWeight: 600 }}>
                    {r.name}
                    {showNoRC && !r.hasRC && <span style={noRcBadge}>sin rate card</span>}
                  </td>
                  <td style={monoTd}>{nf(r.rcTotal)} h</td>
                  <td style={monoTd}>{nf(r.corTotal)} h</td>
                  <td style={{ padding: '10px 14px', minWidth: 180 }}>
                    <div style={{ position: 'relative', height: 8, background: 'var(--card2)', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: `${fill}%`, borderRadius: 4, background: over ? 'var(--red)' : 'var(--green)' }} />
                      {(r.hasRC || !showNoRC) && <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${marker}%`, width: 2, background: 'var(--text2)', opacity: .55 }} />}
                    </div>
                  </td>
                  <td style={{ ...monoTd, fontWeight: 600, color: over ? 'var(--red)' : 'var(--text2)' }}>{signed(r.diff)}</td>
                </tr>
                {isOpen && (
                  <tr>
                    <td colSpan={6} style={{ padding: 0, background: 'var(--card2)' }}>
                      <div style={{ padding: '6px 16px 16px 44px' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr>
                              <th style={subTh}>Persona</th>
                              <th style={{ ...subTh, textAlign: 'right' }}>{planLabel}</th>
                              <th style={{ ...subTh, textAlign: 'right' }}>{realLabel}</th>
                              <th style={{ ...subTh, textAlign: 'right' }}>{diffLabel}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {people.map(x => (
                              <tr key={x.person} style={{ borderTop: '1px solid var(--border)' }}>
                                <td style={{ padding: '7px 10px', fontSize: 13 }}>{x.person}</td>
                                <td style={{ ...monoTd, padding: '7px 10px' }}>{nf(x.rc)} h</td>
                                <td style={{ ...monoTd, padding: '7px 10px' }}>{nf(x.cor)} h</td>
                                <td style={{ ...monoTd, padding: '7px 10px', fontWeight: 600, color: x.diff > 0 ? 'var(--red)' : 'var(--text2)' }}>{signed(x.diff)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {notes?.[r.name] && (
                          <div style={{ marginTop: 10, fontSize: 12, color: 'var(--amber)' }}>⚠ {notes[r.name]}</div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </FragmentRow>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function FragmentRow({ children }) {
  return <>{children}</>;
}

const thStyle = { padding: '11px 14px', textAlign: 'left', fontWeight: 600, fontSize: '10.5px', textTransform: 'uppercase', letterSpacing: '.7px', color: 'var(--text2)', whiteSpace: 'nowrap', position: 'sticky', top: 0, background: 'var(--card2)' };
const subTh = { padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: '.5px', color: 'var(--text3)' };
const monoTd = { padding: '10px 14px', fontFamily: 'DM Mono,monospace', fontSize: 12, whiteSpace: 'nowrap', textAlign: 'right' };
const noRcBadge = { marginLeft: 8, fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', padding: '3px 6px', borderRadius: 5, background: 'var(--amber-bg)', color: 'var(--amber)' };
const calloutStyle = { background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px', fontSize: 13, color: 'var(--text2)', marginBottom: 20, display: 'flex', gap: 10, alignItems: 'flex-start' };
const tagStyle = { flex: 'none', fontSize: 10.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.08em', background: 'var(--amber-bg)', color: 'var(--amber)', padding: '4px 8px', borderRadius: 6 };
